import ArrowLeft from '@ixigo/icons/ArrowLeft';
import ChevronLeft from '@ixigo/icons/ChevronLeft';
import FlightTakeoff from '@ixigo/icons/FlightTakeoff';
import LocationOn from '@ixigo/icons/LocationOn';
import NearMe from '@ixigo/icons/NearMe';
import IconButton from '@ixigo/iui/IconButton';
import Input, { type InputProps } from '@ixigo/iui/Input';
import { ListItemText } from '@ixigo/iui/ListItemText';
import Typography from '@ixigo/iui/Typography';
import { useEventCallback } from '@ixigo/iui/utils';
import {
	ResponsiveModal,
	type ResponsiveModalProps,
} from '@repo/shared/components';
import { Loader } from '@repo/shared/icons';
import cn from '@repo/shared/utils/cn';
import { useDeferredValue, useEffect, useRef, useState } from 'react';
import { getUserLocation, isCoords } from '../location-services';
import { MapLocationPicker, type MapLocationPickerProps } from '../map';
import type { Coords, MapLocation } from '../types';
import { getLocations, getPlaceDetails } from './api';
import { Combobox } from './Combobox';
import type { LocationSuggestion } from './types';
import { type UseComboboxParams, useCombobox } from './useCombobox';

interface LocationPickerProps {
	onClose: () => void;
	activeField?: 'pickup' | 'drop';
	defaultValue?: { pickup?: LocationSuggestion; drop?: LocationSuggestion };
	placeholders?: { pickup?: string; drop?: string };
	onComplete: (value: {
		pickup?: LocationSuggestion;
		drop?: LocationSuggestion;
	}) => void;
	mapApiKey?: MapLocationPickerProps['apiKey'];
}

export interface LocationPickerSheetProps extends LocationPickerProps {
	open: boolean;
	TransitionProps?: ResponsiveModalProps['TransitionProps'];
}

export function LocationPickerSheet({
	open,
	onClose,
	TransitionProps,
	...props
}: LocationPickerSheetProps) {
	useEffect(() => {
		// Request location permission early (cached, with fallback)
		getUserLocation().catch(() => {});
	}, []);

	return (
		<ResponsiveModal
			open={open}
			onClose={onClose}
			fullScreen
			transitionDirection="right"
			TransitionProps={TransitionProps}
		>
			<LocationPicker {...props} onClose={onClose} />
		</ResponsiveModal>
	);
}

const loadOptions = {
	debounceMs: 300,
	minQueryLength: 2,
} satisfies UseComboboxParams<unknown>['loadOptions'];

function getPreloadKeys({
	pickup,
	drop,
}: {
	pickup?: LocationSuggestion;
	drop?: LocationSuggestion;
}): Array<string | undefined> {
	return [pickup, drop].map((location) =>
		isCoords(location) ? `${location.lat},${location.lng}` : location?.placeId,
	);
}

export function LocationPicker(props: LocationPickerProps) {
	const {
		onClose,
		placeholders = { pickup: 'Pickup Location', drop: 'Drop-off Location' },
		onComplete,
		activeField = 'pickup',
		defaultValue,
		mapApiKey,
	} = props;
	const [active, setActive] = useState<'pickup' | 'drop'>(
		activeField ?? 'pickup',
	);
	const [showMap, setShowMap] = useState(false);

	const latestSelectionCoords = useRef<
		Partial<
			Record<
				'pickup' | 'drop',
				Promise<(LocationSuggestion & Coords) | undefined>
			>
		>
	>({});

	const preloadKeys = useRef<Array<string | undefined>>(undefined);
	if (!preloadKeys.current) {
		preloadKeys.current = getPreloadKeys({
			pickup: defaultValue?.pickup,
			drop: defaultValue?.drop,
		});
	}
	// Resolve coords from placeId
	const resolveCoords = useEventCallback(
		async (type: 'pickup' | 'drop', newLocation?: LocationSuggestion) => {
			const location =
				newLocation ??
				(latestSelectionCoords.current[type]
					? undefined
					: fieldConfig[type].value);
			if (location && isMapLocation(location)) {
				latestSelectionCoords.current[type] = isCoords(location)
					? Promise.resolve(location)
					: getPlaceDetails(location.placeId).catch(() => undefined);
			}
			return latestSelectionCoords.current[type];
		},
	);

	const handleSelect = useEventCallback(
		({ value }: { value?: LocationSuggestion }) => {
			if (!value) return;
			const newValue = {
				pickup: pickup.value,
				drop: drop.value,
				[active]: value,
			};
			preloadKeys.current = getPreloadKeys(newValue);
			if (active === 'pickup') {
				if (!drop.value) {
					resolveCoords(active, value);
					setActive('drop');
					drop.inputRef.current?.focus();
					return;
				}
			} else {
				if (!pickup.value) {
					resolveCoords(active, value);
					setActive('pickup');
					pickup.inputRef.current?.focus();
					return;
				}
			}
			onClose();
			onComplete(newValue);
		},
	);

	// Stable load functions - no dependencies on coords

	const loadPickup: typeof getLocations = useEventCallback(
		async (query, opts) => {
			const dropOffCoords = await resolveCoords('drop');
			return getLocations(query, {
				...opts,
				dropOffCoords: dropOffCoords,
			});
		},
	);
	const loadDrop: typeof getLocations = useEventCallback(
		async (query, opts) => {
			const pickupCoords = await resolveCoords('pickup');
			return getLocations(query, {
				...opts,
				pickupCoords: pickupCoords,
			});
		},
	);

	const pickup = useCombobox({
		load: loadPickup,
		itemToString: (item) => item.mainText,
		onChange: handleSelect,
		loadOptions: {
			...loadOptions,
			preload: preloadKeys.current[1] ?? true,
		},
		defaultValue: defaultValue?.pickup,
		clearOnFocus: false,
	});

	const drop = useCombobox({
		load: loadDrop,
		itemToString: (item) => item.mainText,
		onChange: handleSelect,
		loadOptions: {
			...loadOptions,
			preload: preloadKeys.current[0] ?? true,
		},
		defaultValue: defaultValue?.drop,
		clearOnFocus: false,
	});

	// create a record of active field and its combobox
	const fieldConfig = {
		pickup: pickup,
		drop: drop,
	};

	function getInputProps(field: 'pickup' | 'drop'): InputProps {
		const props = fieldConfig[field].getInputProps({
			onFocus: () => setActive(field),
		});
		return {
			...props,
			endAdornment: fieldConfig[field].loading ? (
				<Loader className="text-selection icon-md" />
			) : (
				<Combobox.ClearButton {...fieldConfig[field].getClearProps()} />
			),
			autoFocus: field === active,
			variant: 'outlined',
			placeholder: placeholders?.[field],
			className: 'col-start-2 min-w-0',
			classes: {
				outline: cn('rounded-l-full rounded-r-full border-secondary'),
			},
		};
	}

	const {
		items,
		getListProps,
		getItemProps,
		loading,
		query = '',
	} = fieldConfig[active];

	const hasNoResults = useDeferredValue(
		items.length === 0 &&
			!loading &&
			query.length >= loadOptions.minQueryLength,
	);

	return (
		<div className="flex h-full flex-col overflow-hidden">
			{/* Inputs section */}
			<div className="py-5 px-20 shrink-0 items-start gap-x-1 gap-y-2.5 grid grid-rows-2 grid-cols-[max-content_minmax(0,1fr)] sticky top-0">
				<IconButton
					className="self-center row-start-1 col-start-1"
					edge="start"
					onClick={onClose}
				>
					<ChevronLeft />
				</IconButton>
				<Input {...getInputProps('pickup')} />
				<Input {...getInputProps('drop')} />
			</div>

			<div className="flex-1 overflow-y-auto">
				{/* Use current location row */}
				{mapApiKey && (
					<Combobox.Item onClick={() => setShowMap(true)}>
						<Combobox.Icon>
							<LocationOn />
						</Combobox.Icon>
						<ListItemText primary="Set location on map" />
						<NearMe />
					</Combobox.Item>
				)}

				{/* Section title */}
				{items.length ? (
					<div className="py-10 px-20">
						<Typography variant="h6" className="tracking-[-0.18px]">
							Suggested Locations/ Airports
						</Typography>
					</div>
				) : null}

				<div {...getListProps()}>
					{hasNoResults && (
						<div className="px-20 py-10 text-secondary text-center">
							No suggestions
						</div>
					)}
					{items.map((s, index) => (
						<Combobox.Item
							key={`${s.mainText}-${index}`}
							{...getItemProps({ item: s })}
						>
							<Combobox.Icon>
								{s.type === 'airport' ? <FlightTakeoff /> : <LocationOn />}
							</Combobox.Icon>
							<ListItemText primary={s.mainText} secondary={s.secondaryText} />
						</Combobox.Item>
					))}
				</div>
			</div>
			<ResponsiveModal
				open={showMap}
				onClose={() => setShowMap(false)}
				fullScreen
				transitionDirection="right"
			>
				<MapLocationPicker
					onConfirm={(location) => {
						fieldConfig[active].selectValue({ item: location });
						setShowMap(false);
					}}
					apiKey={mapApiKey}
					defaultValue={
						isMapLocation(fieldConfig[active].value)
							? fieldConfig[active].value
							: undefined
					}
					title={
						active === 'pickup'
							? 'Set your pick-up location'
							: 'Set your destination'
					}
				/>
				<IconButton
					className="bg-primary absolute left-20 top-3 shadow-100 rounded-full"
					onClick={() => setShowMap(false)}
				>
					<ArrowLeft />
				</IconButton>
			</ResponsiveModal>
		</div>
	);
}

function isMapLocation(location?: LocationSuggestion): location is MapLocation {
	return !!(location && (isCoords(location) || location.placeId));
}

export default LocationPickerSheet;
